if (typeof SizeGuideBase === 'undefined') {

    class SizeGuideBase extends HTMLElement {
        static STORAGE_KEY = 'size-guide-state';
        static FRESH_WINDOW_MS = 3000;
        static VARIANT_AJAX_WINDOW_MS = 500;
        static UPDATED_AT_KEY = '_updatedAt';
        static LEGACY_NO_SIZE_SENTINEL = '0';
        static GILET_TOKEN = 'gilet pratique';

        static SELECTORS = {
            variantPicker: 'variant-picker',
            variantOptionPopovers: 'fieldset > .relative > x-popover',
            variantDropdown: '.relative',
            variantOptionInfo: '.variant-picker__option-info',
            sizeGuideIntro: '.size-guide__intro',
            sizeGuideDrawer: 'x-drawer[data-size-guide]',
            sizeGuidePopupPopover: '.size-guide__product-form .relative > x-popover',
            fieldset: 'fieldset',
            popover: 'x-popover',
            optionValue: '.popover__value-option',
            radio: 'input[type="radio"]',
            checkedRadio: 'input[type="radio"]:checked',
            optionPositionInput: 'input[data-option-position]',
            fallback: '[data-size-guide-fallback]',
            braNoMatch: '[data-bra-no-match]',
            variantError: '[data-variant-error]'
        };
        static EVENTS = {
            input: 'input',
            change: 'change',
            click: 'click',
            submit: 'submit',
            variantChange: 'variant:change'
        };
        static ATTRS = {
            ariaSelected: 'aria-selected',
            ariaControls: 'aria-controls',
            open: 'open',
            formId: 'form-id'
        };
        static CLASSES = {
            fallbackError: 'size-guide__fallback--error',
            fieldInvalid: 'size-guide__field--invalid'
        };

        // Cross-instance flag: a size guide is currently driving an AJAX variant change.
        // Set by `_triggerVariantChange`, read by the freshly-connected component to
        // know that storage is the source of truth (not the DOM).
        static _restoring = false;

        /* ─── Lifecycle ──────────────────────────────────────────────────────── */

        constructor () {
            super();
            this.data = [];
            this.formData = {};
            this._variantPopovers = [];
        }

        get storageKey () { return 'default'; }
        get guideType () { return 'default'; }

        connectedCallback () {
            // Restore synchronously BEFORE the first paint — otherwise the browser paints
            // the (stale) server-rendered HTML, then the async CSV resolves and JS fixes it,
            // causing a visible flicker. CSV isn't needed for restoring saved values.
            this._abort = new AbortController();
            this._initVariantPopovers();
            this._capturePopovers();
            this._captureDefaultLabels();
            this._captureFallback();
            this._bindOptionClicks();
            this._bindVariantChangeListener();
            this._bindDocumentVariantChange();
            this._bindFormSubmitGuard();
            this._restore();

            // CSV is only needed for computing new recommendations on user interaction.
            this._loadCsv();
        }

        disconnectedCallback () {
            this._abort?.abort();
        }

        /* ─── Init ──────────────────────────────────────────────────────────── */

        _initVariantPopovers () {
            const picker = this.closest(SizeGuideBase.SELECTORS.variantPicker);
            if (!picker) return;

            // Main variant-picker popovers (desktop / mobile / popup) — exclude ones inside
            // the size guide itself.
            const mainPopovers = Array.from(picker.querySelectorAll(SizeGuideBase.SELECTORS.variantOptionPopovers))
                .filter((popover) => !this.contains(popover));

            // The size-guide drawer ("Guide des tailles" / "Prenez vos mesures") lives
            // outside <variant-picker> as a body-level <x-drawer>. Its own variant size
            // dropdown shares the same `form_id` as the main one, so we treat it as one
            // more popover to drive in sync: hide it when fallback is active, restore
            // it when a real size matches.
            const drawer = document.querySelector(SizeGuideBase.SELECTORS.sizeGuideDrawer);
            const popupPopovers = drawer
                ? Array.from(drawer.querySelectorAll(SizeGuideBase.SELECTORS.sizeGuidePopupPopover))
                : [];

            this._variantPopovers = [...mainPopovers, ...popupPopovers]
                .map((popover) => this._wrapVariantPopover(popover, picker));
        }

        _wrapVariantPopover (popover, picker) {
            // The popover lives either inside the main `<variant-picker>` or inside
            // the size-guide `<x-drawer>` — pick the right scope when looking up the
            // associated `<button>` and its inner `<span>` so we can sync labels
            // from inside `_selectVariant` for both cases.
            const scope = popover.closest(SizeGuideBase.SELECTORS.sizeGuideDrawer) || picker;
            const { btn, span } = this._findButtonAndSpan(popover.id, scope);
            // The dropdown wrapper (`.relative`) hosting the button + popover, and the
            // `.variant-picker__option-info` block hosting the "Votre taille PERCKO
            // adaptée à votre morphologie :" legend. Both get hidden together when the
            // CSV lookup returns text instead of a variant size — the fallback message
            // stands on its own.
            const dropdown = popover.closest(SizeGuideBase.SELECTORS.variantDropdown);
            const fieldset = popover.closest(SizeGuideBase.SELECTORS.fieldset);
            const optionInfo = fieldset?.querySelector(SizeGuideBase.SELECTORS.variantOptionInfo);
            const options = () => Array.from(popover.querySelectorAll(SizeGuideBase.SELECTORS.optionValue));
            return { popover, btn, span, dropdown, optionInfo, options };
        }

        // Hold direct references to the size-guide popovers and a Set of their ids for
        // O(1) ownership checks. On mobile, `<x-popover>` appends itself to <body> when
        // opened — `this.querySelectorAll` can't find it afterwards, but element refs
        // captured at connect time stay valid.
        _capturePopovers () {
            this._popovers = Array.from(this.querySelectorAll(SizeGuideBase.SELECTORS.popover));
            this._popoverIds = new Set(this._popovers.map((p) => p.id));
        }

        // Snapshot the initial (liquid-rendered) button labels for each size guide popover
        // AND for the parent variant-picker dropdown, so we can restore them on reset or
        // when swapping out of fallback mode.
        _captureDefaultLabels () {
            this._defaultLabels = new Map();
            this._popovers.forEach((popover) => {
                const { span } = this._findButtonAndSpan(popover.id);
                if (span) this._defaultLabels.set(popover.id, span.textContent.trim());
            });
            this._variantPickerDefaultLabels = new Map();
            this._variantPopovers.forEach(({ popover, span }) => {
                if (span) this._variantPickerDefaultLabels.set(popover.id, span.textContent.trim());
            });
        }

        // Track two fallback containers in sync:
        //   - The main one, rendered inside `<size-guide-*>`. Relocate it to sit
        //     right after the main variant dropdown so visually it REPLACES the
        //     picker instead of stacking above the legend.
        //   - The popup one inside the size-guide drawer (rendered by
        //     size-guide_popup.liquid). Same text, same visibility — message
        //     stays consistent between the product page and the drawer.
        //
        // The main fallback lookup falls back to the fieldset scope to survive
        // bare re-connects (component disconnected without the parent fieldset
        // being touched — it lives in its moved position then).
        _captureFallback () {
            const mainFallback = this.querySelector(SizeGuideBase.SELECTORS.fallback)
                || this.closest(SizeGuideBase.SELECTORS.fieldset)?.querySelector(SizeGuideBase.SELECTORS.fallback);

            // Relocate the main fallback to sit right after the main dropdown so
            // visually it replaces the picker, not stack above its legend.
            if (mainFallback) {
                const dropdown = this._variantPopovers.find((wrap) => !this._isInDrawer(wrap.dropdown))?.dropdown;
                if (dropdown?.parentNode && mainFallback !== dropdown.nextSibling) {
                    dropdown.parentNode.insertBefore(mainFallback, dropdown.nextSibling);
                }
            }

            // The bra popup owns its own no-match panel (`[data-bra-no-match]`), so for
            // bra products we must NOT also drive the generic drawer fallback — otherwise
            // the popup would render the message twice. Page fallback only in that case.
            const drawer = document.querySelector(SizeGuideBase.SELECTORS.sizeGuideDrawer);
            const drawerOwnsBraNoMatch = !!drawer?.querySelector(SizeGuideBase.SELECTORS.braNoMatch);
            const popupFallback = drawerOwnsBraNoMatch ? null : drawer?.querySelector(SizeGuideBase.SELECTORS.fallback);

            this._fallbackEls = [mainFallback, popupFallback].filter(Boolean);
        }

        _isInDrawer (el) {
            return !!el?.closest(SizeGuideBase.SELECTORS.sizeGuideDrawer);
        }

        _bindOptionClicks () {
            // Mobile (below 1000px): <x-popover> moves its content to <body>, so clicks
            // on popover options no longer bubble through this element. Listen on document
            // and filter by the popover ids this guide owns.
            document.addEventListener(SizeGuideBase.EVENTS.click, this._onOptionClick.bind(this), {
                signal: this._abort.signal
            });
        }

        _bindVariantChangeListener () {
            this._variantPopovers.forEach(({ popover }) => {
                popover.addEventListener(SizeGuideBase.EVENTS.change, this._onVariantChange.bind(this), {
                    signal: this._abort.signal
                });
            });
        }

        // A colour/collar switch re-fetches the section, and Shopify resolves it to a
        // complete variant — so the freshly-rendered fragment auto-checks the first
        // available size and surfaces its code (e.g. "H1.P1"), even though the shopper
        // never picked a size. Listen for the form-level variant:change: when a NON-size
        // option changed and no simulator recommendation is active, clear that auto size
        // back to the placeholder. A genuine size pick (only the size option changes) is
        // left untouched. Runs synchronously before paint, so no flash.
        _bindDocumentVariantChange () {
            document.documentElement.addEventListener(
                SizeGuideBase.EVENTS.variantChange,
                this._onNonSizeVariantChange.bind(this),
                { signal: this._abort.signal }
            );
        }

        _onNonSizeVariantChange (event) {
            if (SizeGuideBase._restoring) return;

            // The event bubbles to the document from every picker on the page (related
            // products, recommendations) — only act on our own product's form. (detail.formId
            // is unreliable: the form's `id` property is shadowed by its `name="id"` input.)
            if (event.target?.getAttribute?.('id') !== this._ownFormId()) return;
            if (this._hasActiveRecommendation()) return;

            const { variant, previousVariant } = event.detail || {};
            if (!variant || !previousVariant) return;
            if (!this._nonSizeOptionChanged(variant, previousVariant)) return;

            this._resetVariantSizeToPlaceholder();
        }

        _ownFormId () {
            return this.closest(SizeGuideBase.SELECTORS.variantPicker)?.getAttribute(SizeGuideBase.ATTRS.formId) || null;
        }

        _hasActiveRecommendation () {
            const size = this._getEntry().recommendation?.[this.guideType];
            return !!size && size !== SizeGuideBase.LEGACY_NO_SIZE_SENTINEL;
        }

        // The 1-based position of the size option, read from a tracked variant popover's
        // radios (the picker uses data-option-position on every option input).
        _sizeOptionPosition () {
            for (const { popover } of this._variantPopovers) {
                const radio = popover.querySelector(SizeGuideBase.SELECTORS.optionPositionInput);
                if (radio) return parseInt(radio.getAttribute('data-option-position'), 10);
            }
            return null;
        }

        _nonSizeOptionChanged (variant, previousVariant) {
            const sizePosition = this._sizeOptionPosition();
            for (let position = 1; position <= 3; position += 1) {
                if (position === sizePosition) continue;
                if ((variant[`option${position}`] ?? null) !== (previousVariant[`option${position}`] ?? null)) {
                    return true;
                }
            }
            return false;
        }

        _resetVariantSizeToPlaceholder () {
            this._clearVariantInvalid();
            this._variantPopovers.forEach(({ popover, span, options }) => {
                options().forEach((opt) => opt.setAttribute(SizeGuideBase.ATTRS.ariaSelected, 'false'));
                popover.querySelectorAll(SizeGuideBase.SELECTORS.radio).forEach((radio) => { radio.checked = false; });
                if (span) span.textContent = span.dataset.sizePlaceholder || '';
            });
        }

        // Block add-to-cart when no size is chosen (else the form adds the default
        // variant) or the simulator found no valid size. Capture phase so we run before
        // <product-form>'s handler. Only the main instance binds — drawer copy has no picker.
        _bindFormSubmitGuard () {
            const picker = this.closest(SizeGuideBase.SELECTORS.variantPicker);
            const formId = picker?.getAttribute(SizeGuideBase.ATTRS.formId);
            const form = formId ? document.getElementById(formId) : null;
            if (!form) return;

            form.addEventListener(SizeGuideBase.EVENTS.submit, (event) => {
                const fallback = this._isFallbackActive();
                const needsSize = this._variantPopovers.length > 0 && !this._isVariantSizeSelected();
                if (!fallback && !needsSize) return;

                event.preventDefault();
                event.stopImmediatePropagation();
                if (fallback) {
                    this._flagFallbackError();
                } else {
                    this._flagMissingVariant();
                }
            }, { capture: true, signal: this._abort.signal });
        }

        _isFallbackActive () {
            return this._fallbackEls?.some((el) => !el.hidden) ?? false;
        }

        // Check both the in-page picker and the drawer: the simulator leaves the drawer radio
        // checked (shared radio group, processed last), and the popup no longer pre-checks a default.
        _isVariantSizeSelected () {
            return this._variantPopovers.some(
                ({ popover }) => popover.querySelector(SizeGuideBase.SELECTORS.checkedRadio)
            );
        }

        // Redden the in-page size dropdown, show the error message and scroll it into view.
        _flagMissingVariant () {
            const dropdowns = this._variantPopovers
                .map(({ dropdown }) => dropdown)
                .filter((dropdown) => dropdown && !this._isInDrawer(dropdown));
            if (!dropdowns.length) return;
            dropdowns.forEach((dropdown) => this._setVariantInvalid(dropdown, true));
            dropdowns[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        _clearVariantInvalid () {
            this._variantPopovers.forEach(({ dropdown }) => {
                if (dropdown) this._setVariantInvalid(dropdown, false);
            });
        }

        _setVariantInvalid (dropdown, invalid) {
            dropdown.classList.toggle(SizeGuideBase.CLASSES.fieldInvalid, invalid);
            const error = dropdown.closest(SizeGuideBase.SELECTORS.fieldset)?.querySelector(SizeGuideBase.SELECTORS.variantError);
            if (error) error.hidden = !invalid;
        }

        /* ─── CSV ───────────────────────────────────────────────────────────── */

        async _loadCsv () {
            const url = this.dataset.csvUrl;
            if (!url) return;

            try {
                const text = await (await fetch(url)).text();
                const [headerLine, ...rows] = text.trim().split(/\r?\n/);
                const headers = this._parseCsvRow(headerLine).map((h) => h.trim());

                this.data = rows.map((row) => {
                    const values = this._parseCsvRow(row);
                    return Object.fromEntries(headers.map((h, i) => [h, values[i]?.trim()]));
                });
            } catch (e) {
                console.error('Failed to load CSV:', e);
            }
        }

        // Minimal RFC-4180 row parser: respects double-quoted fields with embedded
        // commas. Supports `""` as an escaped quote. Needed because the client CSVs now
        // contain quoted fallback strings like
        // `"Pour cette taille, nous vous conseillons le gilet pratique."` whose comma
        // would otherwise split into broken columns.
        _parseCsvRow (row) {
            const out = [];
            let cur = '';
            let inQuotes = false;
            for (let i = 0; i < row.length; i++) {
                const ch = row[i];
                if (ch === '"') {
                    if (inQuotes && row[i + 1] === '"') {
                        cur += '"';
                        i++;
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (ch === ',' && !inQuotes) {
                    out.push(cur);
                    cur = '';
                } else {
                    cur += ch;
                }
            }
            out.push(cur);
            return out;
        }

        /* ─── localStorage ──────────────────────────────────────────────────── */

        _readStore () {
            try { return JSON.parse(localStorage.getItem(SizeGuideBase.STORAGE_KEY) || '{}'); } catch { return {}; }
        }
        _writeStore (root) { localStorage.setItem(SizeGuideBase.STORAGE_KEY, JSON.stringify(root)); }
        _getEntry () { return this._readStore()[this.storageKey] || { selections: {} }; }
        _setEntry (entry) {
            const root = this._readStore();
            root[this.storageKey] = { ...entry, [SizeGuideBase.UPDATED_AT_KEY]: Date.now() };
            this._writeStore(root);
        }

        // Storage is "fresh" if it was written in the last FRESH_WINDOW_MS — meaning the
        // user just made a selection and triggered an AJAX re-render. The new component
        // should trust storage over any transient DOM state during this window.
        _isStorageFresh () {
            const updatedAt = this._getEntry()[SizeGuideBase.UPDATED_AT_KEY];
            return updatedAt && (Date.now() - updatedAt) < SizeGuideBase.FRESH_WINDOW_MS;
        }

        /* ─── Persist ───────────────────────────────────────────────────────── */

        saveState () {
            const selections = {};
            for (const popover of this._popovers) {
                const selected = popover.querySelector(`[${SizeGuideBase.ATTRS.ariaSelected}="true"]`);
                const input = selected?.querySelector('input');
                if (input?.dataset?.field) {
                    selections[input.dataset.field] = { value: input.value, label: selected.textContent.trim() };
                }
            }
            const entry = this._getEntry();
            entry.selections = selections;
            this._setEntry(entry);
        }

        saveRecommendation (size) {
            const entry = this._getEntry();
            if (!entry.recommendation) entry.recommendation = {};
            entry.recommendation[this.guideType] = size;
            this._setEntry(entry);
        }

        /* ─── Restore ───────────────────────────────────────────────────────── */

        _restore () {
            // Always restore the size guide field inputs (visual only, no side-effects).
            this._restoreFromStorage();

            if (this._shouldSkipRecommendation()) return;

            this._restoreRecommendation();
        }

        // Skip the recommendation-driven variant re-selection if the URL already points
        // at a specific variant and nothing tells us it's safe to override. It's safe when:
        //   - storage was just written (user click → AJAX re-render), or
        //   - the stored recommendation matches the variant currently in the URL.
        _shouldSkipRecommendation () {
            const hasUrlVariant = new URLSearchParams(window.location.search).has('variant');
            if (!hasUrlVariant) return false;

            const fresh = this._isStorageFresh() || SizeGuideBase._restoring;
            if (fresh) return false;

            return !this._storedSizeMatchesCurrentVariant();
        }

        _storedSizeMatchesCurrentVariant () {
            const storedSize = this._getEntry().recommendation?.[this.guideType];
            if (!storedSize || !this._variantPopovers.length) return false;

            const [{ popover }] = this._variantPopovers;
            const checked = popover.querySelector(SizeGuideBase.SELECTORS.checkedRadio);
            const currentSize = checked?.closest(SizeGuideBase.SELECTORS.optionValue)?.textContent.trim();

            return !!currentSize && currentSize === storedSize;
        }

        _restoreFromStorage () {
            const { selections } = this._getEntry();
            if (!selections) return;

            for (const [field, { value }] of Object.entries(selections)) {
                this._selectField(field, value);
            }
        }

        _restoreRecommendation () {
            const size = this._readRecommendationWithMigration();
            if (!size || size === SizeGuideBase.LEGACY_NO_SIZE_SENTINEL) return;

            // Re-entry on AJAX re-render: paint directly without dispatching events
            // (the parent variant-picker is mid-teardown — re-emission would re-trigger
            // Shopify's own variant-change cascade). Fallback rendering is event-free —
            // it only paints DOM in our element.
            if (SizeGuideBase._restoring) {
                if (this._isVariantOption(size)) {
                    this._selectVariant(size, false);
                } else {
                    this._showFallback(size);
                    this._hideVariantPicker();
                }
                return;
            }

            this._handleRecommendation(size);
        }

        // Handle both the legacy flat-string shape and the namespaced shape.
        _readRecommendationWithMigration () {
            const entry = this._getEntry();
            if (typeof entry.recommendation === 'string') {
                const size = entry.recommendation;
                entry.recommendation = { [this.guideType]: size };
                this._setEntry(entry);
                return size;
            }
            return entry.recommendation?.[this.guideType];
        }

        // Dispatch a variant change and flag the "AJAX window" so the fresh component's
        // _restore() picks up storage instead of whatever transient DOM state it lands in.
        _triggerVariantChange (size) {
            this._setRestoringFlag(true);
            this._selectVariant(size, true);
            setTimeout(() => this._setRestoringFlag(false), SizeGuideBase.VARIANT_AJAX_WINDOW_MS);
        }

        _setRestoringFlag (value) {
            SizeGuideBase._restoring = value;
        }

        /* ─── Recommendation routing ────────────────────────────────────────── */

        // Single entry point for subclasses to push a CSV lookup result into the UI.
        // If `size` matches a real variant option in the picker, drive the variant
        // picker (existing behavior). Otherwise treat it as a fallback message and
        // render it via `_showFallback`, hiding the variant dropdown so it doesn't show
        // a stale previously-recommended size (the "cache" bug in the Asana brief).
        _handleRecommendation (size) {
            if (!size || size === SizeGuideBase.LEGACY_NO_SIZE_SENTINEL) {
                this._clearFallback();
                this._restoreVariantPicker();
                return;
            }

            this.saveRecommendation(size);
            this.saveState();

            if (this._isVariantOption(size)) {
                this._clearFallback();
                this._restoreVariantPicker();
                this._triggerVariantChange(size);
            } else {
                this._clearVariantSelection();
                this._showFallback(size);
                this._hideVariantPicker();
            }
        }

        _isVariantOption (value) {
            if (!this._variantPopovers.length) return false;
            return this._variantPopovers[0].options().some(
                (opt) => opt.textContent.trim() === value
            );
        }

        /* ─── Fallback rendering ────────────────────────────────────────────── */

        // Paint the fallback text into every captured `_fallbackEls` (main page +
        // size-guide drawer). When the simulator has a `data-gilet-pratique-url`
        // attribute AND the text mentions "gilet pratique", linkify that token —
        // that's the redirection the brief asks for on the T-shirt discret pages.
        // The bra simulator omits the URL and just shows the raw "Taille non
        // disponible" text.
        _showFallback (text) {
            if (!this._fallbackEls?.length) return;
            const url = this.dataset.giletPratiqueUrl;
            const safeText = this._escapeHtml(text);
            const shouldLinkify = url && text.toLowerCase().includes(SizeGuideBase.GILET_TOKEN);

            this._fallbackEls.forEach((el) => {
                if (shouldLinkify) {
                    const tokenRegex = new RegExp(this._escapeRegExp(SizeGuideBase.GILET_TOKEN), 'i');
                    el.innerHTML = safeText.replace(
                        tokenRegex,
                        `<a href="${this._escapeHtml(url)}">${SizeGuideBase.GILET_TOKEN}</a>`
                    );
                } else {
                    el.textContent = text;
                }
                el.classList.remove(SizeGuideBase.CLASSES.fallbackError);
                el.hidden = false;
            });
        }

        _clearFallback () {
            this._fallbackEls?.forEach((el) => {
                el.hidden = true;
                el.textContent = '';
                el.classList.remove(SizeGuideBase.CLASSES.fallbackError);
            });
        }

        // Mark the fallback message as an error (red). Called when the user attempts
        // to add to cart while the simulator is in fallback state.
        _flagFallbackError () {
            this._fallbackEls?.forEach((el) => {
                if (!el.hidden) el.classList.add(SizeGuideBase.CLASSES.fallbackError);
            });
        }

        // We use inline `style.display` rather than the `[hidden]` attribute because
        // the theme's `.variant-picker__option-info { display: flex }` rule
        // (assets/theme.css) beats the user-agent stylesheet's `[hidden]` rule, and
        // we don't want to fight CSS specificity on every override we'd add.
        _hideVariantPicker () { this._setVariantPickerDisplay('none'); }
        _restoreVariantPicker () { this._setVariantPickerDisplay(''); }

        // Reflect the bra popup calculator's result on the PRODUCT PAGE: show the
        // "Taille non disponible" fallback and hide the page picker/intro when no size
        // matches, or clear it when one is found. Page-only by design — the drawer popup
        // owns its own no-match panel, so we never touch drawer elements here. Event-free:
        // the caller (bra popup) owns variant selection on the matching path.
        setPageNoSize (text) {
            if (text) {
                this._clearVariantSelection();
                this._showFallback(text);
                this._setPageVariantPickerDisplay('none');
            } else {
                this._clearFallback();
                this._setPageVariantPickerDisplay('');
            }
        }

        _setPageVariantPickerDisplay (value) {
            this._variantPopovers
                .filter(({ dropdown }) => dropdown && !this._isInDrawer(dropdown))
                .forEach(({ dropdown }) => { dropdown.style.display = value; });
            const intro = this.parentElement?.querySelector(SizeGuideBase.SELECTORS.sizeGuideIntro);
            if (intro) intro.style.display = value;
            const optionInfo = this.closest(SizeGuideBase.SELECTORS.fieldset)
                ?.querySelector(SizeGuideBase.SELECTORS.variantOptionInfo);
            if (optionInfo) optionInfo.style.display = value;
        }

        // Toggle visibility of:
        //   - every variant dropdown the size guide drives (`.relative` wrapper)
        //   - the option-info legend (`<legend>` "Taille" / "Size") if the theme
        //     renders one
        //   - the size-guide intro span ("Votre taille PERCKO adaptée à votre
        //     morphologie :") rendered by variant-size-guide.liquid as a sibling
        //     of this custom element inside the `.size-guide` wrapper
        // The intro span is the actual user-visible "title" — easy to confuse with
        // the variant-picker's legend, but they're separate elements.
        _setVariantPickerDisplay (value) {
            this._variantPopovers.forEach(({ dropdown }) => {
                if (dropdown) dropdown.style.display = value;
            });
            this._getSwappableLabels().forEach((el) => { el.style.display = value; });
        }

        _getSwappableLabels () {
            const els = [];
            const seen = new Set();
            const add = (el) => {
                if (el && !seen.has(el)) {
                    seen.add(el);
                    els.push(el);
                }
            };

            // The size option's legend block, if rendered.
            const ownFieldset = this.closest(SizeGuideBase.SELECTORS.fieldset);
            add(ownFieldset?.querySelector(SizeGuideBase.SELECTORS.variantOptionInfo));

            // The "Votre taille PERCKO adaptée à votre morphologie :" intro span —
            // sibling of `this` inside the `.size-guide` wrapper.
            add(this.parentElement?.querySelector(SizeGuideBase.SELECTORS.sizeGuideIntro));

            // Defensive: per-popover refs from `_initVariantPopovers` (mobile/popup
            // variant-picker instances if they ever land in the same query result).
            this._variantPopovers.forEach(({ optionInfo }) => add(optionInfo));
            return els;
        }

        // Reset the variant picker visuals only — the user-entered measurements should
        // stay visible while we tell them no size matches. This is the missing reset
        // that caused the "cached previous proposition" bug.
        _clearVariantSelection () {
            this._variantPopovers.forEach(({ popover, span, options }) => {
                options().forEach((opt) => opt.setAttribute(SizeGuideBase.ATTRS.ariaSelected, 'false'));
                popover.querySelectorAll(SizeGuideBase.SELECTORS.radio).forEach((r) => { r.checked = false; });
                if (span) {
                    const defaultLabel = this._variantPickerDefaultLabels?.get(popover.id);
                    span.textContent = defaultLabel || '';
                }
            });
        }

        /* ─── HTML helpers ──────────────────────────────────────────────────── */

        _escapeHtml (s) {
            return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        }
        _escapeRegExp (s) {
            return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }

        /* ─── Popover helpers ───────────────────────────────────────────────── */

        // Buttons for the size guide popovers stay inside this element even when the
        // popovers themselves get moved to <body> on mobile. Scope lookup here by default.
        _findButtonAndSpan (popoverId, scope = this) {
            const btn = scope.querySelector(`button[${SizeGuideBase.ATTRS.ariaControls}='${popoverId}']`);
            const span = btn?.querySelector(`#${popoverId}-selected-value`);
            return { btn, span };
        }

        _markOptionSelected (option, popover) {
            popover.querySelectorAll(SizeGuideBase.SELECTORS.optionValue)
                .forEach((o) => o.setAttribute(SizeGuideBase.ATTRS.ariaSelected, 'false'));
            option.setAttribute(SizeGuideBase.ATTRS.ariaSelected, 'true');

            const { span } = this._findButtonAndSpan(popover.id);
            if (span) span.textContent = option.textContent.trim();
        }

        // Queries stored popover refs rather than this.querySelector, so it still works
        // after mobile popovers get moved out of this element.
        _findFieldInput (field, value) {
            for (const popover of this._popovers) {
                const input = popover.querySelector(`input[data-field="${field}"][value="${value}"]`);
                if (input) return { input, popover };
            }
            return { input: null, popover: null };
        }

        /* ─── Field selection (inside size guide popovers) ─────────────────── */

        _selectField (field, value) {
            const { input, popover } = this._findFieldInput(field, value);
            if (!input || !popover) return;

            this.formData[field] = value;

            const option = input.closest(SizeGuideBase.SELECTORS.optionValue);
            if (!option) return;

            this._markOptionSelected(option, popover);
        }

        /* ─── Variant picker (all instances: desktop, mobile, popup) ───────── */

        _selectVariant (size, dispatchEvents = true) {
            if (!this._variantPopovers.length) return;
            this._clearVariantInvalid();

            for (const { span, options } of this._variantPopovers) {
                const allOptions = options();
                const match = allOptions.find((opt) => opt.textContent.trim() === size);
                if (!match) continue;

                const input = match.querySelector('input');
                if (!input) continue;

                const alreadyChecked = input.checked;
                allOptions.forEach((opt) => {
                    const radio = opt.querySelector('input');
                    if (radio) radio.checked = (radio === input);
                });

                if (dispatchEvents && !alreadyChecked) {
                    input.dispatchEvent(new Event(SizeGuideBase.EVENTS.input, { bubbles: true }));
                    input.dispatchEvent(new Event(SizeGuideBase.EVENTS.change, { bubbles: true }));
                }

                if (span) span.textContent = size;
            }
        }

        // Legacy alias kept for any external caller — internally subclasses go through
        // `_handleRecommendation`, which handles both real-size and fallback paths.
        updateSiblingPopover (recommendedSize) {
            this._handleRecommendation(recommendedSize);
        }

        /* ─── Event handling ────────────────────────────────────────────────── */

        _onOptionClick (event) {
            const option = event.target.closest(SizeGuideBase.SELECTORS.optionValue);
            if (!option) return;

            const popover = option.closest(SizeGuideBase.SELECTORS.popover);
            if (!popover || !this._popoverIds.has(popover.id)) return;

            this._markOptionSelected(option, popover);
            popover.removeAttribute(SizeGuideBase.ATTRS.open);

            this.updateFormData(option.firstElementChild);
        }

        // Fired whenever any variant-picker radio changes. Distinguishes programmatic
        // selection (triggered by us via `_selectVariant`, flagged `_restoring`) from a
        // real user click on the variant picker — only the latter should wipe the guide.
        _onVariantChange () {
            if (SizeGuideBase._restoring) return;
            this._resetAndFlush();
        }

        /* ─── Reset ─────────────────────────────────────────────────────────── */

        _resetAndFlush () {
            this._clearStorageEntry();
            this.formData = {};
            this._clearFieldSelections();
            this._clearVariantInvalid();
            this._restoreDefaultLabels();
            this._clearFallback();
            this._restoreVariantPicker();
        }

        _clearStorageEntry () {
            const root = this._readStore();
            delete root[this.storageKey];
            this._writeStore(root);
        }

        _clearFieldSelections () {
            this._popovers.forEach((popover) => {
                popover.querySelectorAll(SizeGuideBase.SELECTORS.optionValue)
                    .forEach((option) => option.setAttribute(SizeGuideBase.ATTRS.ariaSelected, 'false'));
                popover.querySelectorAll(SizeGuideBase.SELECTORS.radio)
                    .forEach((radio) => { radio.checked = false; });
            });
        }

        _restoreDefaultLabels () {
            this._defaultLabels?.forEach((label, popoverId) => {
                const { span } = this._findButtonAndSpan(popoverId);
                if (span) span.textContent = label;
            });
        }

        /* ─── Override in subclass ──────────────────────────────────────────── */
        updateFormData () { }
    }

    window.SizeGuideBase = SizeGuideBase;
}
